import React, { Fragment } from 'react';
import styled from 'styled-components';
import { object, string, number, node } from 'prop-types';

import { Experiment, Variant } from '@olxbr/react-experimentation';
import withVisibilityToggle from '../../../core/enhancements/withVisibilityToggle';
import CheckoutButton from './CheckoutButtonContainer';
import VisibilityInformation from './VisibilityInformation';
import FreeRenewalMessage from './FreeRenewalMessage';
import Price from './Price';
import Characteristics from './CharacteristicsContainer';
import InstallmentPrice from './InstallmentPrice';
import DiscountTag from './DiscountTagContainer';
import extraAdIcon from '../../images/extra-ad.svg';
import highlightSilverIcon from '../../images/highlight-silver.svg';
import highlightGoldIcon from '../../images/highlight-gold.svg';
import highlightDiamondIcon from '../../images/highlight-diamond.svg';
import newHighlightDiamondIcon from '../../images/new-highlight-diamond.svg';
import highlightBronzeIcon from '../../images/highlight-bronze.svg';
import visibilityRenewIcon from '../../images/visibility-renew.svg';

const ItemContainer = styled.article`
  box-sizing: border-box;
  display: block;
  padding: 8px 16px;
  @media (min-width: 1000px) {
    vertical-align: top;
    display: inline-block;
    width: ${props => `${props.width}%`};
    padding: 0 8px;
  }
`;

const StyledPackageBox = styled.div`
  background: #fff;
  border-radius: 8px;
  color: #323232;
  margin: 10px 0 0;
  padding-bottom: 20px;
  text-align: center;
  border: solid 1px #e5e5e5;
  position: relative;
  box-shadow: ${({ blueShadow }) => (blueShadow ? '0 0 14px 0 rgba(42,196,236,0.25)' : '')};
`;

const Title = styled.h2`
  font-size: 14px;
  font-weight: bold;
  text-transform: uppercase;
  line-height: 1.71;
  text-align: center;
  margin-bottom: ${({ isDiamond }) => (isDiamond ? '4px' : '8px')};
`;

const RecommendedBadge = styled.span`
  background-color: #ffffff;
  border-radius: 100px;
  border: solid 1px #e5e5e5;
  color: ${({ label }) => (label ? label.color : '#6e0ad6')};
  font-size: 9px;
  font-weight: bold;
  padding: 4px 10px;
  text-transform: uppercase;
  position: absolute;
  right: 0;
  left: 0;
  margin: auto;
  width: fit-content;
  top: -11px;
`;

const CheckoutButtonWrapper = styled.div`
  padding: 12px 32px;
`;

const Icon = styled.div`
  font-size: 14px;
  font-weight: bold;
  padding: 24px 0 8px 0;
  text-transform: uppercase;
  line-height: 1.71;
  text-align: center;
  > img {
    display:inline;
  }
  
  ${({ type }) =>
    (type === 'PACKAGE_DIAMOND' ? 'position: relative; top: -5px; padding: 0px' : '')}
`;

const RecommendedLabel = withVisibilityToggle(RecommendedBadge);
const DiscountLabel = withVisibilityToggle(DiscountTag);

const icons = {
  PACKAGE_SILVER: highlightSilverIcon,
  PACKAGE_GOLD: highlightGoldIcon,
  PACKAGE_GOLD_BIWEEKLY: highlightGoldIcon,
  PACKAGE_DIAMOND: highlightDiamondIcon,
  PACKAGE_DIAMOND_MONTHLY: newHighlightDiamondIcon,
  PACKAGE_BRONZE: highlightBronzeIcon,
  PAID_RENEWAL: extraAdIcon,
  FREE_RENEWAL: extraAdIcon,
  PAID_VISIBILITY_RENEWAL: visibilityRenewIcon,
  FREE_VISIBILITY_RENEWAL: visibilityRenewIcon,
  PAID_AD: extraAdIcon,
  BUNDLE_HV_GOLD: highlightGoldIcon,
  BUNDLE_HV_GOLD_BIWEEKLY: highlightGoldIcon,
  BUNDLE_HV_DIAMOND_MONTHLY: newHighlightDiamondIcon,
};

const PackageBox = ({ children, type }) => (
  <Fragment>
    <Experiment name="osp-visual-highlight">
      <Variant value="show">
        <StyledPackageBox blueShadow={type === 'PACKAGE_DIAMOND'}>
          {children}
        </StyledPackageBox>
      </Variant>
      <Variant default>
        <StyledPackageBox>
          {children}
        </StyledPackageBox>
      </Variant>
    </Experiment>
  </Fragment>
);

PackageBox.propTypes = {
  children: node.isRequired,
  type: string.isRequired,
};

const ProductItem = ({
  product, posName, posDiscount, width, categoryBalance
}) => {
  const {
    fullName,
    recommended,
    label,
    buyButton,
    title,
    amount,
    totalPrice: price,
    characteristics,
    discount,
    totalReferencePrice: referencePrice,
    visibility,
    type,
    installment,
    message
  } = product;

  const isDiamond = type === 'PACKAGE_DIAMOND';
  const isBuyRecommended = buyButton && buyButton.recommended;
  const hasInstallmentToShow = typeof installment === 'object';
  const newCharacteristics = [
    ...characteristics,
    {
      checkmark: isDiamond,
      link: 'superShowcase',
      text: isDiamond ? '7 dias com Super Vitrine *NOVO*' : '~~7 dias com Super Vitrine~~ *NOVO*',
      visualHighlight: true,
    },
  ];

  if (isDiamond) {
    newCharacteristics[1] = {
      ...newCharacteristics[1],
      text: '7 dias com o selo **Super Vitrine**',
    };
  }

  return (
    <ItemContainer data-e2e="subscription-item" width={width}>
      <PackageBox type={type}>
        <RecommendedLabel label={label} showIf={label}>
          {(label && label.text) || 'Mais Vendido'}
        </RecommendedLabel>
        <DiscountLabel
          showIf={discount}
          recommended={recommended || label}
          discount={discount}
        />

        <Icon>
          <img src={icons[type]} alt={title} />
        </Icon>
        <Title>{fullName}</Title>

        {message
          ? <FreeRenewalMessage message={message} availableLimit={categoryBalance} />
          : <VisibilityInformation text={visibility.text} />}

        {!message && <Fragment>
          {<Price value={price} posDiscount={posDiscount} visible={!hasInstallmentToShow} />}
          {hasInstallmentToShow && <InstallmentPrice value={price} posDiscount={posDiscount} installment={installment} />}
        </Fragment>}

        <CheckoutButtonWrapper>
          <CheckoutButton
            fullWidth
            type={isBuyRecommended ? 'primary' : 'secondary'}
            product={product}
          />
        </CheckoutButtonWrapper>
        <Characteristics
          characteristics={characteristics}
          price={price}
          amount={amount}
          referencePrice={referencePrice}
          product={type}
          posName={posName}
        />
      </PackageBox>
    </ItemContainer>
  );
};

ProductItem.propTypes = {
  product: object.isRequired,
  posName: string.isRequired,
  posDiscount: number,
  width: number.isRequired,
  categoryBalance: number,
};

ProductItem.defaultProps = {
  posDiscount: 0,
  categoryBalance: 0,
};

export default ProductItem;
